<template>
  <div class="relative">
    <Loader v-if="isLoading" class="loader--page"></Loader>
    <div class="container">
      <div class="p-booking__inner-wrapper">
        <div class="s-hero-slider__title mb-3">
          {{ dictionary.title }}
        </div>
        <div v-if="currentStep === 1" class="p-booking__desc">
          {{ dictionary.cinemaTitle }}
        </div>
        <div v-if="currentStep === 2" class="p-booking__title pt-4">
          {{ dictionary.paymentTitle }}
        </div>
        <div v-if="currentStep === 2" class="p-booking__title-desc mb-4">
          {{ dictionary.paymentDescription }}
        </div>

        <Alert
          v-if="!isLoading && errorMsgs.length"
          key="submit-errors"
          dismiss-by-native=""
          type="danger"
          :messages="errorMsgs"
          @close="removeErrors"></Alert>

        <CinemaChoiceOverlay
          v-if="!disabledCanteen"
          :title="dictionary.cinemaTitle"
          :placeholder="dictionary.cinemaPlaceholder"
          :cinemas="cinemas"
          :label="dictionary.cinemaOptionLabel"
          @choose-cinema="chooseCinema" />

        <!-- Banner -->
        <div v-if="shopBanner && currentStep === 1" class="p-booking__banner">
          <a
            v-if="shopBanner.url"
            :href="shopBanner.url"
            target="_blank"
            rel="noopener noreferrer">
            <picture>
              <source
                v-if="shopBanner.mobile"
                :srcset="shopBanner.mobile.src"
                :media="`(max-width: 768px)`" />
              <img
                v-if="shopBanner.desktop"
                :src="shopBanner.desktop.src"
                :alt="shopBanner.desktop.alt || 'Special Offer'" />
            </picture>
          </a>
          <picture v-else>
            <source
              v-if="shopBanner.mobile"
              :srcset="shopBanner.mobile.src"
              :media="`(max-width: 768px)`" />
            <img
              v-if="shopBanner.desktop"
              :src="shopBanner.desktop.src"
              :alt="shopBanner.desktop.alt || 'Special Offer'" />
          </picture>
        </div>

        <div class="p-booking__tickets-wrapper" :class="disabledCanteenClass">
          <div class="p-booking__tickets-choice">
            <ShopItems
              v-if="currentStep === 1"
              class="mt-3"
              :shope-items="sortedProduct">
              <template #remove="{ product }">
                <button
                  :key="selectedProducts[product.id]"
                  type="button"
                  class="btn-icon btn--minus-icon"
                  :disabled="store.getSelectedProductCounter(product) === 0"
                  aria-label="remove product from cart"
                  @click="() => store.removeUserProduct(product)"></button>
              </template>
              <template #counter="{ product }">
                {{ store.getSelectedProductCounter(product) }}
              </template>
              <template #add="{ product }">
                <button
                  class="btn-icon btn-plus-icon"
                  type="button"
                  aria-label="add product to cart"
                  :disabled="isDisabledAdd(product)"
                  @click="() => store.addNewUserProduct(product)"></button>
              </template>
            </ShopItems>

            <template v-if="currentStep === 2">
              <form
                ref="form"
                autocomplete="off"
                @submit.prevent="onSubmitForm">
                <PaymentDetails
                  v-if="showPaymentDetails"
                  :additional-payload="additionalPayload"
                  @change-input="onChangeInput" />
                <UserProfile v-if="showUserProfile" @click-edit="onClickEdit" />

                <div class="mt-4">
                  <div class="custom-chck custom-chck--additional-style">
                    <input
                      id="user-terms"
                      v-model="terms"
                      type="checkbox"
                      :class="{ 'form-control--error': v$.terms.$error }"
                      @blur="v$.terms.$touch"
                      @change="onTermsChange" />
                    <label for="user-terms">
                      <span class="custom-chck__span">
                        <div class="custom-chck__text">
                          <em>{{ dictionary.termsHyperLabel }}*</em>
                          <span v-html="dictionary.termsLabel"></span>
                        </div>
                      </span>
                    </label>
                  </div>
                  <template v-if="v$.terms.$error">
                    <div
                      v-if="v$.terms.checked.$invalid"
                      class="field-validation-error">
                      {{ dictionary.termsRequired }}
                    </div>
                  </template>
                  <button
                    :id="buttonPaymentSubmitId"
                    type="submit"
                    class="d-none"
                    :disabled="buttonSubmitIsDisabled"></button>
                </div>
              </form>
            </template>
          </div>

          <div class="p-booking__tickets-total">
            <div class="p-booking__shop__summary">
              <CartSummary :is-shopping-cafe="true" />

              <CartPoints></CartPoints>

              <div class="p-booking__shop__warning">
                {{ dictionary.glutenWarning }}
              </div>

              <AddCreditVoucher v-if="currentStep === 2" />
            </div>
          </div>
        </div>
      </div>
      <Otp
        ref="otp"
        :dictionary="otpSettings.dictionary"
        :configuration="otpSettings.configuration"
        @success="triggerSubmit"
        @otp-blocked-error="handleBlockedErrorOtp"
        @loading="
          (value) => {
            isLoading = value
          }
        " />
    </div>
    <div>
      <ShopCafeProgress
        v-if="disabledCanteen"
        :current-step="currentStep"
        @go-to-next="goToNextStep"
        @restart-booking="restartBooking"
        @remove-cinema-choice="removeCinema" />
    </div>
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue'
import {
  getData,
  scrollToError,
  scrollToTop,
  scrollToValidation,
} from '../../lib/lib'
import ShopItems from '../components/Booking/Step4/ShopItems.vue'
import CartSummary from '../components/Booking/Shared/CartSummary.vue'
import CinemaChoiceOverlay from '../components/CinemaChoiceOverlay.vue'
import ShopCafeProgress from '../components/ShopCafeProgress.vue'
import PaymentDetails from '../components/Booking/Step5/PaymentDetails.vue'
import UserProfile from '../components/Booking/Step5/UserProfile.vue'
import AddCreditVoucher from '../components/AddCreditVoucher.vue'
import Otp from '../components/Otp.vue'
import { useBookingStore } from '../store/BookingStore'
import { useVuelidate } from '@vuelidate/core'
import Loader from '../components/Shared/Loader.vue'
import Alert from '../components/Shared/Alert.vue'
import * as lib from './../../lib/lib'
import constants from '../../lib/constants'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import CartPoints from '../components/Booking/Shared/CartPoints.vue'
export default {
  components: {
    ShopItems,
    CartSummary,
    CinemaChoiceOverlay,
    ShopCafeProgress,
    PaymentDetails,
    AddCreditVoucher,
    Loader,
    Alert,
    UserProfile,
    Otp,
    CartPoints,
  },
  setup() {
    return { store: useBookingStore(), v$: useVuelidate() }
  },
  data() {
    return {
      dictionary: null,
      shopItems: null,
      cinemas: null,
      currentStep: 1,
      additionalPayload: null,

      editProfileEnabled: false,

      terms: false,
      isLoading: true,
      errorMsgs: [],
      buttonPaymentSubmitId: constants.buttonPaymentSubmitId,

      otpSettings: null,
      otpVerificationCode: '',
      shopBanner: null,
    }
  },
  validations() {
    return {
      terms: {
        checked: (value) => value === true,
      },
    }
  },
  computed: {
    selectedProducts() {
      return this.store.getAllSelectedProducts
    },
    disabledCanteen() {
      return Object.keys(this.store.getSelectedCinema).length
    },

    disabledCanteenClass() {
      return !this.disabledCanteen && 'b-cinema-overlay__disabled-content'
    },

    showPaymentDetails() {
      return this.store.accountIsInValidForPayment || this.editProfileEnabled
    },

    showUserProfile() {
      return !this.store.accountIsInValidForPayment && !this.editProfileEnabled
    },

    buttonSubmitIsDisabled() {
      return this.isLoading || this.store.bookingProgress.paymentDetails.invalid
    },

    sortedProduct() {
      // sort groups by groupId
      const sorted = [...this.store.shopItems].sort(
        (a, b) => a.values[0].groupId - b.values[0].groupId,
      )

      // sort products by sortOrder
      sorted.forEach((item) => {
        item.values.sort((a, b) => a.sortOrder - b.sortOrder)
      })

      return sorted
    },
  },
  beforeMount() {
    const { dictionary } =
      getCurrentInstance().appContext.config.globalProperties
    this.dictionary = dictionary
    const { shopItems, cinemas, otp, shopBanner } = getData('shoppingCafeData')

    this.shopItems = shopItems
    this.store.setShopItems(this.shopItems)
    this.cinemas = cinemas
    this.otpSettings = otp
    this.shopBanner = shopBanner || null

    const { userSessionId } = this.configuration
    this.store.setUsersessionId(userSessionId)
  },

  async mounted() {
    await this.checkLogin()
    this.checkedIfLoggedIn()

    document.addEventListener('login-was-held', (e) => {
      const { accountData } = e.detail
      this.store.setAccountData(accountData)
      this.store.setPaymentDetails(accountData)
      this.fetchShopItems()
    })
  },

  methods: {
    isDisabledAdd(item) {
      const { maxItemsPerProduct } = this.configuration
      const availablePoints = this.store.checkAvailablePoints(item.points)

      return (
        this.store.getSelectedProductCounter(item) >= maxItemsPerProduct ||
        !availablePoints
      )
    },

    async fetchShopItems() {
      this.isLoading = true
      this.removeErrors()

      const {
        language,
        endpointFetchShopItems,
        fetchShopItemsCaptchaAction,
        refresh,
      } = this.configuration

      const postData = {
        language,
        userSessionId: this.store.bookingProgress.usersessionId,
        isVillageShop: true,
      }

      await postRequestHandler({
        postData,
        endpoint: endpointFetchShopItems,
        successCallback: this.fetchShopItemsHandleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: refresh,
        captchaAction: fetchShopItemsCaptchaAction,
      })
    },

    fetchShopItemsHandleSuccess(response) {
      this.handleSuccess()
      const { shopItems, userSessionId } = response.data
      this.shopItems = shopItems
      this.store.setShopItems(shopItems)
      this.store.setShopItemsByCinema()
      this.store.setUsersessionId(userSessionId)
    },

    chooseCinema(cinema) {
      this.store.selectCinemaInCafe(cinema)
      this.store.setShopItems(this.shopItems)
      this.store.setShopItemsByCinema()
    },

    checkedIfLoggedIn() {
      const isLoggedIn = this.store.accountData?.memberId

      if (!isLoggedIn) {
        jQuery(document).trigger('login-clicked')
      }
    },

    removeCinema() {
      this.store.removeCinema()
      this.store.setShopItems(this.shopItems)
      this.store.clearUserProducts()
    },

    onTermsChange() {
      this.store.setPaymentField({ field: 'terms', value: this.terms })
      this.onChangeInput()
    },

    async goToNextStep() {
      if (this.currentStep === 2) {
        await this.$nextTick()
        this.$refs['otp'].start(this.store.bookingProgress.paymentDetails.email)
      } else {
        await this.postAddConcessionsRequest()
        // this.currentStep = 2
        scrollToTop()
      }
    },

    async triggerSubmit(verificationCode) {
      this.otpVerificationCode = verificationCode
      await this.$nextTick()
      this.onSubmitForm()
    },

    async restartBooking() {
      const postData = {
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointRestartBooking,
        successCallback: () => {
          window.location.reload()
        },
        errorCallback: () => {
          alert('An error occured')
        },
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaRestartBooking,
      })
    },
    onChangeInput() {
      this.store.setPaymentField({
        field: 'invalid',
        value: this.v$.$invalid,
      })
    },

    async postAddConcessionsRequest() {
      this.isLoading = true
      this.removeErrors()

      const postData = {
        cinemaId: this.store.bookingProgress.selectedCinema.value,
        concessions: Object.keys(this.selectedProducts).map((id) => ({
          itemId: id,
          quantity: this.selectedProducts[id].count,
          recognitionId: this.selectedProducts[id].recognitionId,
        })),
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointAddShopItems,
        successCallback: this.concessionsRequestSuccess,
        errorCallback: this.concessionsRequestError,
        captchaAction: this.configuration.addShopItemsCaptchaAction,
        refreshTokenHandler: this.configuration.refresh,
      })
    },

    concessionsRequestSuccess() {
      this.isLoading = false
      this.errorMsgs = []
      this.currentStep = 2
    },

    concessionsRequestError(messages) {
      this.errorMsgs = messages
      if (!messages) {
        this.showError(this.dictionary.defaultErrorMessage)
      } else {
        this.showError(messages)
      }
      this.isLoading = false
      this.$nextTick(() => {
        scrollToTop()
      })
    },

    async checkLogin() {
      return new Promise((resolve, reject) => {
        this.isLoading = true
        this.errorMsgs = []
        const postData = {}

        postRequestHandler({
          postData,
          endpoint: this.configuration.endpointCheckLogin,

          successCallback: (response) => {
            this.checkLoginHandleSuccess(response)
            resolve()
          },
          errorCallback: (messages) => {
            this.handleError(messages)
            reject()
          },
          captchaAction: this.configuration.checkLogincaptchaAction,
          refreshTokenHandler: this.configuration.refresh,
        })
      })
    },

    checkLoginHandleSuccess(response) {
      this.handleSuccess()
      const { accountData } = response.data
      if (!accountData) {
        return false
      }
      this.store.setAccountData(accountData)
      this.store.setPaymentDetails(accountData)
      this.onChangeInput()
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      if (!messages) {
        this.showError(this.dictionary.defaultErrorMessage)
      } else {
        this.showError(messages)
      }
      this.isLoading = false
      this.$nextTick(() => {
        scrollToTop()
      })
    },

    showError(mess) {
      if (typeof mess === 'string') {
        this.errorMsgs = [mess]
      } else {
        this.errorMsgs = [...mess]
      }
    },

    removeErrors() {
      this.errorMsgs = []
    },

    async onClickEdit() {
      this.editProfileEnabled = !this.editProfileEnabled
      await this.$nextTick()
      lib.scrollToElem(jQuery('.b-payment-details'), 0)
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        await this.$nextTick()
        scrollToValidation()
        return false
      }

      const { firstName, lastName, email, mobilePhone, invalid, terms } =
        this.store.bookingProgress.paymentDetails

      if (invalid) {
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        mobilePhone: mobilePhone,
        verificationCode: this.otpVerificationCode,
        culture: document.documentElement.lang,
        acceptTerms: terms,
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleFinalSubmitSuccess,
        errorCallback: this.handleFinalSubmitError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.submitOrdercaptchaAction,
      })
    },
    handleFinalSubmitSuccess(response) {
      this.isLoading = false
      this.removeErrors()

      const { redirectUrl } = response.data

      if (redirectUrl) {
        this.isLoading = true
        window.location.assign(redirectUrl)
      }
    },

    async handleFinalSubmitError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage,
      )

      await this.$nextTick()
      scrollToError()
    },

    handleBlockedErrorOtp() {
      this.restartBooking()
    },
  },
}
</script>

<style scoped lang="scss">
.btn-print-state {
  position: fixed;
  bottom: 10em;
  right: 1em;
  padding: 8px;
  font-size: 0.65em;
  z-index: 10;
}
</style>
